import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@ankr.com/ui';
import {
  Accordion,
  AccordionSummary,
  Button,
  AccordionDetails,
  Typography,
} from '@mui/material';

import { useAccount } from 'modules/account/hooks/useAccount';
import { useThemes } from 'uiKit/Theme/hook/useThemes';

import { useAccordionNavButtonStyles } from './useAccordionNavButtonStyles';
import { NavigationItem } from '../BaseNavButton';

interface IAccordionNavButtonProps {
  buttonItem: NavigationItem;
  isMobileSideBar: boolean;
  onClick: () => void;
}

export const AccordionNavButton = ({
  buttonItem,
  isMobileSideBar,
  onClick,
}: IAccordionNavButtonProps) => {
  const { isLightTheme } = useThemes();
  const { pathname } = useLocation();
  const { permissions } = useAccount();

  const { classes, cx } = useAccordionNavButtonStyles({
    isLightTheme,
    isMobileSideBar,
  });

  const { StartIcon, internalItems, isDisabled, isHidden, label } = buttonItem;

  const nestedPaths = useMemo(() => {
    return (
      internalItems
        ?.map(internalItem => internalItem.items)
        .flat()
        .map(nestedItem => nestedItem.href) ?? []
    );
  }, [internalItems]);

  const isNestedButtonActive = useCallback(
    (currentPathname: string) => {
      return currentPathname === pathname;
    },
    [pathname],
  );

  const isAccordionButtonActive = useMemo(() => {
    return nestedPaths.some(path => path === pathname);
  }, [nestedPaths, pathname]);

  const [isExpanded, setIsExpanded] = useState(isAccordionButtonActive);

  const onToggleAccordion = useCallback(() => {
    setIsExpanded(prevValue => !prevValue);
  }, []);

  useEffect(() => {
    if (isAccordionButtonActive) setIsExpanded(true);
  }, [isAccordionButtonActive]);

  if (isHidden) return null;

  return (
    <Accordion className={classes.root} expanded={isExpanded}>
      <AccordionSummary
        className={cx(
          classes.summaryRoot,
          isAccordionButtonActive && classes.activeLink,
        )}
        classes={{
          content: classes.summaryContent,
          expanded: classes.summaryExpanded,
        }}
        expandIcon={<ChevronRightIcon />}
        onClick={onToggleAccordion}
      >
        <Button
          className={cx(
            classes.commonButton,
            classes.mainButton,
            isDisabled && classes.disabled,
          )}
          variant="text"
          startIcon={<StartIcon />}
        >
          {label}
        </Button>
      </AccordionSummary>
      <AccordionDetails className={classes.detailsRoot}>
        {internalItems?.map(mainItem => (
          <div key={mainItem?.title}>
            {mainItem.title && (
              <Typography variant="subtitle3" color="textSecondary">
                {mainItem.title}
              </Typography>
            )}
            {mainItem.items.map(innerItem => {
              const hasAccess = permissions.includes(
                innerItem.requiresPermission,
              );

              if (innerItem.isHidden) {
                return null;
              }

              return (
                <Button
                  className={cx(
                    classes.commonButton,
                    isNestedButtonActive(innerItem.href)
                      ? classes.activeLink
                      : classes.ordinaryButton,
                    { [classes.disabled]: innerItem.isDisabled },
                  )}
                  component={hasAccess ? NavLink : Button}
                  key={innerItem.label}
                  to={hasAccess ? innerItem.href : undefined}
                  onClick={hasAccess ? onClick : innerItem.onAccessDeniedClick}
                  disabled={innerItem.isDisabled}
                  variant="text"
                >
                  <Typography variant="button3">{innerItem.label}</Typography>
                </Button>
              );
            })}
          </div>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};
